import React, { useState } from 'react';
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  IconButton,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import PrintRoundedIcon from '@mui/icons-material/PrintRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const highlightText = (text, query) => {
  const safeText = String(text || '');
  const safeQuery = String(query || '').trim();
  if (!safeQuery) return safeText;

  const lower = safeText.toLowerCase();
  const lowerQuery = safeQuery.toLowerCase();
  const index = lower.indexOf(lowerQuery);
  if (index === -1) return safeText;

  return (
    <>
      {safeText.slice(0, index)}
      <Box component="span" sx={{ bgcolor: 'warning.light', px: 0.2, borderRadius: 0.5 }}>
        {safeText.slice(index, index + safeQuery.length)}
      </Box>
      {safeText.slice(index + safeQuery.length)}
    </>
  );
};

const formatPercentage = (value) => (typeof value === 'number' ? `${value.toFixed(1)}%` : '-');

const SkeletonRow = ({ compact = false }) => (
  <TableRow>
    {!compact && <TableCell padding="checkbox"><Skeleton variant="rounded" width={18} height={18} /></TableCell>}
    {!compact && <TableCell padding="checkbox"><Skeleton variant="rounded" width={18} height={18} /></TableCell>}
    <TableCell><Skeleton height={20} width={40} /></TableCell>
    <TableCell><Skeleton height={20} /></TableCell>
    <TableCell><Skeleton height={20} /></TableCell>
    <TableCell align="center"><Skeleton height={20} /></TableCell>
    <TableCell align="center"><Skeleton height={20} /></TableCell>
    <TableCell align="center"><Skeleton height={20} /></TableCell>
    <TableCell align="center"><Skeleton height={20} /></TableCell>
    {!compact && <TableCell align="right"><Skeleton height={20} width={70} /></TableCell>}
  </TableRow>
);

const CompactSubjectRows = ({ rows, searchTerm, downloadingReportCard, onPrintReportCard, onViewStudent }) => (
  <>
    {rows.map((row) => {
      const isDownloading = String(downloadingReportCard) === String(row.studentId);
      return (
        <TableRow key={row.rowId} hover>
          <TableCell sx={{ fontWeight: 700 }}>{row.rollNumber || '-'}</TableCell>
          <TableCell>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                {highlightText(row.studentName, searchTerm)}
              </Typography>
            </Box>
          </TableCell>
          <TableCell>{row.classSection}</TableCell>
          <TableCell>{highlightText(row.subjectName, searchTerm)}</TableCell>
          <TableCell align="center">
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {row.marksObtained ?? '-'} / {row.totalMarks ?? '-'}
            </Typography>
          </TableCell>
          <TableCell align="center">{formatPercentage(row.percentage)}</TableCell>
          <TableCell align="center">{row.grade || '-'}</TableCell>
          <TableCell align="center">
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: row.status === 'Pass' ? 'success.main' : row.status === 'Fail' ? 'error.main' : 'text.secondary',
              }}
            >
              {row.status}
            </Typography>
          </TableCell>
          <TableCell align="right">
            <Stack direction="row" spacing={0.35} justifyContent="flex-end">
              <Tooltip title="View details">
                <IconButton size="small" onClick={() => onViewStudent(row)}>
                  <VisibilityRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Print report card">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => onPrintReportCard(row)}
                    disabled={isDownloading}
                  >
                    {isDownloading ? <CircularProgress size={16} /> : <PrintRoundedIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      );
    })}
  </>
);

const StudentResultsSimpleTable = ({
  loading,
  rows,
  selectedStudentIds = [],
  onToggleStudent,
  onToggleAll,
  allVisibleSelected,
  searchTerm,
  downloadingReportCard,
  onPrintReportCard,
  onViewStudent,
  compact = false,
}) => {
  const [expandedRows, setExpandedRows] = useState({});
  const visibleStudentIds = Array.from(new Set(rows.map((row) => String(row.studentId))));
  const isSummaryMode = !compact && rows.some((row) => Array.isArray(row.subjectRows));

  const toggleExpanded = (studentId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [studentId]: !prev[studentId],
    }));
  };

  return (
    <TableContainer sx={{ maxHeight: compact ? 420 : 'calc(100vh - 310px)' }}>
      <Table stickyHeader size="small">
        <TableHead>
          {isSummaryMode ? (
            <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 800, whiteSpace: 'nowrap' } }}>
              <TableCell padding="checkbox">
                <Checkbox
                  indeterminate={selectedStudentIds.length > 0 && !allVisibleSelected}
                  checked={Boolean(allVisibleSelected && visibleStudentIds.length)}
                  onChange={onToggleAll}
                  size="small"
                />
              </TableCell>
              <TableCell padding="checkbox" />
              <TableCell>Roll No</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell align="center">Marks Obtained</TableCell>
              <TableCell align="center">Percentage</TableCell>
              <TableCell align="center">Grade</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          ) : (
            <TableRow sx={{ '& th': { bgcolor: 'grey.100', fontWeight: 800, whiteSpace: 'nowrap' } }}>
              {!compact && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedStudentIds.length > 0 && !allVisibleSelected}
                    checked={Boolean(allVisibleSelected && visibleStudentIds.length)}
                    onChange={onToggleAll}
                    size="small"
                  />
                </TableCell>
              )}
              <TableCell>Roll No</TableCell>
              <TableCell>Student Name</TableCell>
              <TableCell>Class &amp; Section</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell align="center">Marks Obtained</TableCell>
              <TableCell align="center">Percentage</TableCell>
              <TableCell align="center">Grade</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          )}
        </TableHead>
        <TableBody>
          {loading ? (
            [...Array(6)].map((_, index) => <SkeletonRow key={index} compact={compact && !isSummaryMode} />)
          ) : rows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={isSummaryMode ? 11 : compact ? 9 : 10} align="center" sx={{ py: 6 }}>
                <Stack alignItems="center" spacing={1} sx={{ color: 'text.secondary' }}>
                  <InfoOutlinedIcon sx={{ fontSize: 44 }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {isSummaryMode ? 'No matching students' : 'No matching result rows'}
                  </Typography>
                  <Typography variant="body2">
                    Adjust the filters or search term to see more records.
                  </Typography>
                </Stack>
              </TableCell>
            </TableRow>
          ) : isSummaryMode ? (
            rows.map((row) => {
              const isSelected = selectedStudentIds.includes(String(row.studentId));
              const isDownloading = String(downloadingReportCard) === String(row.studentId);
              const isExpanded = Boolean(expandedRows[row.studentId]);
              const subjectRows = Array.isArray(row.subjectRows) ? row.subjectRows : [];

              return (
                <React.Fragment key={row.rowId}>
                  <TableRow hover selected={isSelected} sx={{ '& td': { py: 1.1 } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={isSelected}
                        onChange={() => onToggleStudent(row.studentId)}
                      />
                    </TableCell>
                    <TableCell padding="checkbox">
                      <IconButton size="small" onClick={() => toggleExpanded(row.studentId)}>
                        {isExpanded ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{row.rollNumber || '-'}</TableCell>
                    <TableCell>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                          {highlightText(row.studentName, searchTerm)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {row.classSection || '-'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {row.marksObtained ?? '-'} / {row.totalMarks ?? '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{formatPercentage(row.percentage)}</TableCell>
                    <TableCell align="center">{row.grade || '-'}</TableCell>
                    <TableCell align="center">
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700,
                          color: row.status === 'Pass' || row.status === 'Passed'
                            ? 'success.main'
                            : row.status === 'Fail' || row.status === 'Failed'
                              ? 'error.main'
                              : 'text.secondary',
                        }}
                      >
                        {row.status}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.35} justifyContent="flex-end">
                        <Tooltip title={isExpanded ? 'Hide subjects' : 'Show subjects'}>
                          <IconButton size="small" onClick={() => toggleExpanded(row.studentId)}>
                            {isExpanded ? <KeyboardArrowUpRoundedIcon fontSize="small" /> : <KeyboardArrowDownRoundedIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="View details">
                          <IconButton size="small" onClick={() => onViewStudent(row)}>
                            <VisibilityRoundedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Print report card">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => onPrintReportCard(row)}
                              disabled={isDownloading}
                            >
                              {isDownloading ? <CircularProgress size={16} /> : <PrintRoundedIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ p: 0, borderBottom: isExpanded ? undefined : 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ px: 2, py: 1.5, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>
                            Subject Percentage Breakdown
                          </Typography>
                          <Table size="small" sx={{ bgcolor: 'common.white', borderRadius: 2 }}>
                            <TableHead>
                              <TableRow sx={{ '& th': { fontWeight: 800, bgcolor: 'grey.100' } }}>
                                <TableCell>Subject</TableCell>
                                <TableCell align="center">Marks</TableCell>
                                <TableCell align="center">Percentage</TableCell>
                                <TableCell align="center">Grade</TableCell>
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {subjectRows.map((subjectRow) => (
                                <TableRow key={subjectRow.rowId} hover>
                                  <TableCell>{highlightText(subjectRow.subjectName, searchTerm)}</TableCell>
                                  <TableCell align="center">
                                    {subjectRow.marksObtained ?? '-'} / {subjectRow.totalMarks ?? '-'}
                                  </TableCell>
                                  <TableCell align="center">{formatPercentage(subjectRow.percentage)}</TableCell>
                                  <TableCell align="center">{subjectRow.grade || '-'}</TableCell>
                                  <TableCell align="center">
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontWeight: 700,
                                        color: subjectRow.status === 'Pass'
                                          ? 'success.main'
                                          : subjectRow.status === 'Fail'
                                            ? 'error.main'
                                            : 'text.secondary',
                                      }}
                                    >
                                      {subjectRow.status}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })
          ) : (
            <CompactSubjectRows
              rows={rows}
              searchTerm={searchTerm}
              downloadingReportCard={downloadingReportCard}
              onPrintReportCard={onPrintReportCard}
              onViewStudent={onViewStudent}
            />
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default StudentResultsSimpleTable;
